import PropTypes from "prop-types";
import React, { Component } from "react";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { Toolbar, ToolbarGroup } from "material-ui/Toolbar";
import Paper from "material-ui/Paper";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { makeStyles } from "@material-ui/core/styles";
import Checkbox from "material-ui/Checkbox";
import CircularProgress from "material-ui/CircularProgress";
import theme from "styles/theme";
import IndeterminateCheckBox from "material-ui/svg-icons/toggle/indeterminate-check-box";
import CheckBox from "material-ui/svg-icons/toggle/check-box";
import CloseIcon from "material-ui/svg-icons/navigation/close";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import { FormatMessage } from "react-globalize";
import { format } from "lib/globalize-util";
import { CatalogAddItem, BulkCatalogAddItem } from "components/catalog-add-item";
import ExportItems from "components/export-items";
import shallowequal from "shallowequal";
import { Confirm } from "components/dialog";
import { merge, $, media } from "glamor";
import { VIEWPORT_PHABLET, VIEWPORT_MOBILE } from "constants/media";
import Badge from "material-ui/Badge";
import moment from "moment";
import { DATATEST } from "constants/integration";

const styles = {
    base: {
        display: "flex",
        height: `${theme.appBar.height}px`,
        position: "fixed",
        top: (theme.appBar.height + 16) * -1 + "px",
        overflow: "hidden",
        left: "0px",
        right: "0px",
        zIndex: 1010,
        transition: "all .2s ease-in-out",
    },
    baseOpen: {
        top: "0px",
        overflow: "auto",
    },
    basePanelOpen: {
        right: `${theme.sidebar.width}px`,
    },
    selectAll: {
        base: {
            marginLeft: "20px",
        },
        desktop: {
            marginLeft: "50px",
        },
    },
    actionLabel: {
        display: "inline-block",
        lineHeight: 1,
        margin: "0px",
        paddingRight: "6px",
        verticalAlign: "middle",
        position: "relative",
        top: "-10px",
        fontSize: "16px",
        color: "#676767",
    },
    checkbox: {
        marginTop: "4px",
        verticalAlign: "middle",
    },
    checkboxMobile: {
        marginTop: "0px",
    },
    checkboxIcon: {
        marginTop: "4px",
        verticalAlign: "middle",
    },
    checkboxLabel: {
        lineHeight: "16px",
        fontSize: "14px",
        color: "#676767",
        marginTop: "5px",
        width: "225px",
    },
    toolbar: {
        backgroundColor: theme.palette.lightestGrey,
        height: `${theme.appBar.height}px`,
        width: "100%",
    },
    toolbarGroup: {
        paddingTop: "4px",
    },
    count: {
        base: {
            fontSize: "22px",
            verticalAlign: "middle",
            lineHeight: `${theme.appBar.height}px`,
            color: "#676767",
            marginLeft: "20px",
            transition: "all .2s ease-in-out",
            flexShrink: 0,
        },
        desktop: {
            marginLeft: "200px",
        },
    },
    countHide: {
        opacity: 0,
    },
    button: {
        marginTop: "4px",
        marginLeft: "8px",
        verticalAlign: "middle",
    },
    mobileBadge: {
        padding: "0px",
        top: "20px",
        marginBottom: 20,
    },
    menuItem: {
        container: {
            lineHeight: "32px",
            minHeight: "32px",
        },
        chevronLeftContainer: {
            marginRight: 15,
            minWidth: "auto",
        },
        chevronRightContainer: {
            marginLeft: 15,
            minWidth: "auto",
        },
        chevronLeft: {
            margin: "4px 12px",
        },
        chevronRight: {
            margin: "4px 12px",
        },
        leftAlignedContent: {
            padding: "0px 72px 0px 24px",
        },
        backItemContent: {
            padding: "0px 24px 0px 48px",
            fontSize: "14px",
        },
    },
};

const useStyles = makeStyles({
    svgIcon: {
        fill: "#333",
        fontSize: "24px",
    },
    menuItem: {
        lineHeight: "32px",
        minHeight: "32px",
        padding: "0 24px",
    },
    menuItemText: {
        fontSize: "15px",
        fontFamily: "Lato, sans-serif",
    },
    actionLabel: {
        display: "inline-block",
        lineHeight: 1,
        margin: "0px",
        paddingRight: "6px",
        verticalAlign: "middle",
        position: "relative",
        fontSize: "16px",
        color: "#676767",
    },
});

const ActionsMenu = (props) => {
    const { viewport, options, positionOptions, positionMenuOpen } = props;
    const isPhabletOrLess = [VIEWPORT_MOBILE, VIEWPORT_PHABLET].includes(viewport);

    const classes = useStyles();

    // const [open, setOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleOpen = React.useCallback((e) => {
        // setOpen(true);
        setAnchorEl(e.currentTarget);
    }, []);

    const handleClose = React.useCallback(() => {
        // setOpen(false);
        setAnchorEl(null);
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleOptionClick = (option) => {
        if (!option.keepMenuOpen) {
            handleClose();
        }

        option.onClick();
    };

    const defaultActionsList = React.useMemo(() => {
        return options
            .filter((option) => option.display)
            .map((option) => (
                <MenuItem
                    className={classes.menuItem}
                    key={option.key}
                    onClick={() => handleOptionClick(option)}
                    data-test={option.dataTest}
                >
                    <ListItemText primary={option.label} className={classes.menuItemText} disableTypography={true} />
                    {option.rightIcon}
                </MenuItem>
            ));
    }, [options, classes, handleOptionClick]);

    const positionsList = React.useMemo(() => {
        return positionOptions
            ?.filter((option) => option.display)
            ?.map((option) => (
                <MenuItem
                    className={classes.menuItem}
                    key={option.key}
                    onClick={() => handleOptionClick(option)}
                    data-test={option.dataTest}
                >
                    {option.leftIcon}
                    <ListItemText primary={option.label} className={classes.menuItemText} disableTypography={true} />
                </MenuItem>
            ));
    }, [positionOptions, classes, handleOptionClick]);

    return (
        <div>
            {!isPhabletOrLess && <label className={classes.actionLabel}>{format("Actions")}:</label>}
            <IconButton
                data-test={DATATEST.GALLERY.BULK_MORE_ACTIONS}
                aria-label="more"
                aria-controls="long-menu"
                aria-haspopup="true"
                onClick={handleOpen}
            >
                <MoreVertIcon className={classes.svgIcon} />
            </IconButton>
            <Menu
                id="long-menu"
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                keepMounted
                open={!!anchorEl}
                onClose={handleClose}
            >
                {positionMenuOpen ? positionsList : defaultActionsList}
            </Menu>
        </div>
    );
};

class BulkActions extends Component {
    static contextTypes = {
        me: PropTypes.object.isRequired,
        app: PropTypes.object.isRequired,
        router: PropTypes.object.isRequired,
    };

    static propTypes = {
        viewport: PropTypes.string.isRequired,
        onClear: PropTypes.func.isRequired,
        onDeleteSelectedEntries: PropTypes.func,
        onSelectAll: PropTypes.func.isRequired,
        catalog: PropTypes.object,
        entities: PropTypes.object.isRequired,
        type: PropTypes.oneOf(["item", "catalog"]).isRequired,
        activeList: PropTypes.object.isRequired,
        onActiveListAdd: PropTypes.func.isRequired,
        onActiveListRemove: PropTypes.func.isRequired,
        onActiveListFetchEntries: PropTypes.func.isRequired,
        onSetActiveList: PropTypes.func,
        bulkItems: PropTypes.object,
        scope: PropTypes.object.isRequired,
        flags: PropTypes.object.isRequired,
        sortBy: PropTypes.string,
        sortOrder: PropTypes.string,
        onSnackbarAdd: PropTypes.func.isRequired,
        viewAs: PropTypes.object,
        viewAsGuest: PropTypes.bool,
        activeBrand: PropTypes.object,
        onFetchDeliveryWindows: PropTypes.func.isRequired,
        deliveryWindows: PropTypes.array.isRequired,
        fetchingWindows: PropTypes.bool.isRequired,
    };

    displayCount = 0;

    styles = merge(
        $(" .mobileSelectedCount", {
            display: "none !important",
        }),
        $(".mobileBulkActionCheckbox", {
            display: "none !important",
        }),
        media(
            "(min-width: 240px) AND (max-width: 466px)",
            $(" .bulkActionCheckbox", {
                display: "none !important",
            }),
            $(" .mobileBulkActionCheckbox", {
                display: "flex !important",
            }),
            $(" .mobileBulkActionCheckbox label", {
                width: "80px !important",
            })
        ),
        media(
            "(min-width: 467px)",
            $(" .mobileBulkActionCheckbox", {
                display: "none !important",
            })
        ),
        media(
            "(min-width: 240px) AND (max-width: 595px)",
            $(" .selectedCount", {
                display: "none",
            }),
            $(" .mobileSelectedCount", {
                display: "inline-block !important",
            })
        )
    );

    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            addToCatalogOpen: false,
            positionMenuOpen: false,
            positionToAdd: null,
            catalogType: "custom-list",
            exportOpen: false,
            confirmDeleteOpen: false,
        };
    }

    shouldComponentUpdate(nextProps, nextState, nextContext) {
        return (
            !shallowequal(this.props, nextProps) ||
            !shallowequal(this.state, nextState) ||
            !shallowequal(this.context, nextContext)
        );
    }

    UNSAFE_componentWillMount() {
        if (!this.props.scope.brand?.getPreference("hide_predefined_delivery_windows")) {
            this.props.onFetchDeliveryWindows();
        }
    }

    handleOrder = () => {
        const { app, me } = this.context;
        const { bulkItems, onClear, type, searchTerm, filters, catalog, viewAs, viewAsGuest, entities } = this.props;
        let query = {};

        if (bulkItems.allSelected) {
            query = filters ? filters.toJS() : {};
            const active = query.__active;
            delete query.__active;
            switch (active) {
                case "all":
                    query.__view_disabled = true;
                    break;
                case "inactive":
                    query.__view_disabled = true;
                    query.__active = false;
                    break;
                case "active":
                default:
                    query.__view_disabled = false;
            }
            if (searchTerm) {
                query.__search = searchTerm;
            }
            if (viewAs) {
                query.__company = viewAs._id;
            }
            if (viewAsGuest) {
                query.__guest = "true";
            }
        }

        const catalogSettings = {};
        if (catalog) {
            catalogSettings.ship_start = moment.utc(catalog.delivery_window_start).valueOf();
            catalogSettings.ship_end = moment.utc(catalog.delivery_window_end).valueOf();
            catalogSettings.prebook = catalog.prebook ? true : false;
            const catalogFilters = filters ? filters.toJS() : {};
            catalogSettings.enforce_delivery_windows = catalogFilters.enforce_delivery_windows;
        }

        Object.assign(
            query,
            bulkItems.toFilters((id) => (type === "item" ? id : entities.entries[id].item_id))
        );

        app.trigger("add-to-cart", {
            params: {
                query: query,
                type: type === "catalog" ? "catalog" : "items",
                catalog_id: type === "catalog" ? catalog.id : undefined,
                ...catalogSettings,
            },
        });

        me.trackAction("Add to order", {
            selectAll: bulkItems.allSelected,
            resource: "bulk",
            area: type === "catalog" ? "catalog" : "gallery",
            bulk: true,
        });

        onClear();
    };

    handleCloseAddToCatalog = () => {
        const { flags: { addProductToLinesheetModalNu67306: isNewModalEnabled } = {} } = this.props;
        this.setState({ addToCatalogOpen: false, positionToAdd: null });
        if (!isNewModalEnabled) {
            this.props.onClear();
        }
    };

    handleOpenAddToCatalog = (positionToAdd = null) => {
        this.setState({ addToCatalogOpen: true, positionToAdd, positionMenuOpen: false });
    };

    handleChangeActionMenuState = ({ catalogType, positionMenuOpen } = {}) => {
        const { flags: { addProductToLinesheetModalNu67306: isNewModalEnabled } = {} } = this.props;
        this.setState((prevState) => ({
            catalogType: catalogType || prevState.catalogType,
            positionMenuOpen: isNewModalEnabled
                ? false
                : positionMenuOpen === undefined
                  ? !prevState.positionMenuOpen
                  : positionMenuOpen,
            ...(isNewModalEnabled && { addToCatalogOpen: true }),
        }));
    };

    handleOpenExportDialog = () => {
        this.setState({ exportOpen: true });
    };

    handleCloseExportDialog = () => {
        this.setState({ exportOpen: false });
    };

    handleConfirmDelete = () => {
        this.setState({ confirmDeleteOpen: true });
    };

    handleCancelDelete = () => {
        this.setState({ confirmDeleteOpen: false });
    };

    handeConfirmDelete = () => {
        const { bulkItems, onClear, onDeleteSelectedEntries, searchTerm, filters } = this.props;
        let query = {};

        if (bulkItems.allSelected) {
            query = filters ? filters.toJS() : {};
            const active = query.__active;
            delete query.__active;
            switch (active) {
                case "all":
                    query.__view_disabled = true;
                    break;
                case "inactive":
                    query.__view_disabled = true;
                    query.__active = false;
                    break;
                case "active":
                default:
                    query.__view_disabled = false;
            }
            if (searchTerm) {
                query.__search = searchTerm;
            }
        }
        Object.assign(
            query,
            bulkItems.toFilters((x) => x, "id")
        );

        this.setState({ confirmDeleteOpen: false });
        onClear();
        onDeleteSelectedEntries(query);
    };

    renderActions = () => {
        const { me } = this.context;
        const {
            type,
            catalog,
            viewport,
            flags: { addProductToLinesheetModalNu67306: isNewModalEnabled } = {},
            scope,
        } = this.props;
        const canExportItems = me.hasPermission("export_product_data");
        const linesheetTerm = scope.brand.hasPreference("linesheets_are_catalogs")
            ? format("Catalog")
            : format("Linesheet");

        const options = [
            {
                key: "addToOrder",
                label: format("Add to Order"),
                display: me.hasPermission("can_order"),
                onClick: this.handleOrder,
                dataTest: DATATEST.GALLERY.BULK_ADD_TO_ORDER,
            },
            {
                key: "addToList",
                label: format("Add to List"),
                display: true,
                keepMenuOpen: isNewModalEnabled ? false : true,
                ...(!isNewModalEnabled && {
                    rightIcon: (
                        <ListItemIcon style={styles.menuItem.chevronRightContainer}>
                            <ChevronRight />
                        </ListItemIcon>
                    ),
                }),
                onClick: () => this.handleChangeActionMenuState({ catalogType: "custom-list" }),
                dataTest: DATATEST.GALLERY.BULK_ADD_TO_LIST,
            },
            {
                key: "addToLinesheet",
                label: format(`Add to ${linesheetTerm}`),
                display: me.hasPermission("create_linesheet"),
                keepMenuOpen: isNewModalEnabled ? false : true,
                ...(!isNewModalEnabled && {
                    rightIcon: (
                        <ListItemIcon style={styles.menuItem.chevronRightContainer}>
                            <ChevronRight />
                        </ListItemIcon>
                    ),
                }),
                onClick: () => this.handleChangeActionMenuState({ catalogType: "linesheet" }),
                dataTest: DATATEST.GALLERY.BULK_ADD_TO_CATALOG,
            },
            {
                key: "export",
                label: format("Export"),
                display: canExportItems && type === "item",
                onClick: this.handleOpenExportDialog,
                dataTest: DATATEST.GALLERY.BULK_EXPORT,
            },
            {
                key: "delete",
                label: format("Delete Selected"),
                display: catalog && catalog._editable && !catalog.isProcessing?.(),
                onClick: this.handleConfirmDelete,
                dataTest: DATATEST.GALLERY.BULK_DELETE_SELECTED,
            },
        ];

        const positionOptions = isNewModalEnabled
            ? []
            : [
                  {
                      key: "return-back",
                      label: format("Back"),
                      display: true,
                      keepMenuOpen: true,
                      leftIcon: (
                          <ListItemIcon style={styles.menuItem.chevronLeftContainer}>
                              <ChevronLeft />
                          </ListItemIcon>
                      ),
                      style: styles.menuItem.container,
                      onClick: () => this.handleChangeActionMenuState(),
                  },
                  {
                      key: "add-to-top",
                      label: format("Add to Top"),
                      display: true,
                      style: styles.menuItem.container,
                      onClick: () => this.handleOpenAddToCatalog("top"),
                      dataTest: DATATEST.GALLERY.BULK_ADDTO_TOP,
                  },
                  {
                      key: "add-to-bottom",
                      label: format("Add to Bottom"),
                      display: true,
                      style: styles.menuItem.container,
                      onClick: () => this.handleOpenAddToCatalog("bottom"),
                      dataTest: DATATEST.GALLERY.BULK_ADDTO_BOTTOM,
                  },
              ];

        return (
            <ActionsMenu
                options={options}
                positionOptions={positionOptions}
                viewport={viewport}
                positionMenuOpen={this.state.positionMenuOpen}
            />
        );
    };

    render() {
        const {
            activeBrand,
            activeList,
            bulkItems,
            catalog,
            filters,
            onActiveListAdd,
            onActiveListFetchEntries,
            onActiveListRemove,
            onClear,
            onSelectAll,
            onSetActiveList,
            searchTerm,
            sidePanelOpen,
            scope,
            sortBy,
            sortOrder,
            templates,
            atsCustomTemplates,
            type,
            entities,
            onSnackbarAdd,
            viewport,
            viewAs,
            deliveryWindows,
            fetchingWindows,
            onFetchDeliveryWindows,
            flags: { addProductToLinesheetModalNu67306: isNewModalEnabled } = {},
        } = this.props;
        const { loading, addToCatalogOpen, positionToAdd, catalogType, exportOpen, confirmDeleteOpen } = this.state;

        const isPhabletOrLess = viewport === VIEWPORT_MOBILE || viewport === VIEWPORT_PHABLET;

        let right;
        if (loading) {
            right = <CircularProgress size={16} thickness={1} />;
        } else {
            right = this.renderActions();
        }

        if (!bulkItems.allSelected) {
            this.displayCount = bulkItems.size;
        }

        return (
            <Paper
                {...this.styles}
                zDepth={2}
                style={Object.assign(
                    {},
                    styles.base,
                    bulkItems.size > 0 && styles.baseOpen,
                    !isPhabletOrLess && sidePanelOpen && styles.basePanelOpen
                )}
            >
                {isNewModalEnabled ? (
                    <BulkCatalogAddItem
                        catalogType={catalogType}
                        currentCatalog={catalog}
                        filters={filters}
                        type={type}
                        entities={entities}
                        onActiveListAdd={onActiveListAdd}
                        onActiveListFetchEntries={onActiveListFetchEntries}
                        onActiveListRemove={onActiveListRemove}
                        onRequestClose={this.handleCloseAddToCatalog}
                        onSetActiveList={onSetActiveList}
                        open={addToCatalogOpen}
                        searchTerm={searchTerm}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSnackbarAdd={onSnackbarAdd}
                    />
                ) : (
                    <CatalogAddItem
                        activeList={activeList}
                        bulkItems={bulkItems}
                        catalogType={catalogType}
                        currentCatalog={catalog}
                        filters={filters}
                        type={type}
                        entities={entities}
                        onActiveListAdd={onActiveListAdd}
                        onActiveListFetchEntries={onActiveListFetchEntries}
                        onActiveListRemove={onActiveListRemove}
                        onRequestClose={this.handleCloseAddToCatalog}
                        onSetActiveList={onSetActiveList}
                        open={addToCatalogOpen}
                        positionToAdd={positionToAdd}
                        searchTerm={searchTerm}
                        scope={scope}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSnackbarAdd={onSnackbarAdd}
                        viewport={viewport}
                        deliveryWindows={deliveryWindows}
                        fetchingWindows={fetchingWindows}
                    />
                )}

                <ExportItems
                    activeBrand={activeBrand}
                    templates={templates}
                    atsCustomTemplates={atsCustomTemplates}
                    bulkItems={bulkItems}
                    filters={filters}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    type={type}
                    entities={entities}
                    searchTerm={searchTerm}
                    open={exportOpen}
                    onRequestClose={this.handleCloseExportDialog}
                    viewAs={viewAs}
                    scope={scope}
                    deliveryWindows={deliveryWindows}
                />
                <Confirm
                    open={confirmDeleteOpen}
                    onConfirm={this.handeConfirmDelete}
                    onCancel={this.handleCancelDelete}
                >
                    {catalog && catalog.type === "linesheet"
                        ? format("Are you sure you want to delete selected items from linesheet?", {
                              linesheet:
                                  scope.brand && scope.brand.hasPreference("linesheets_are_catalogs")
                                      ? format("catalog")
                                      : format("linesheet"),
                          })
                        : format("Are you sure you want to delete selected items from this list?")}
                </Confirm>
                <Toolbar style={styles.toolbar}>
                    <ToolbarGroup firstChild={true}>
                        <IconButton style={styles.button} onClick={onClear}>
                            <CloseIcon
                                // eslint-disable-next-line import/no-named-as-default-member
                                color={theme.rawTheme.palette.primary2Color}
                                // eslint-disable-next-line import/no-named-as-default-member
                                hoverColor={theme.rawTheme.palette.primary1Color}
                            />
                        </IconButton>
                    </ToolbarGroup>
                    <ToolbarGroup
                        className="bulkActionCheckbox"
                        style={Object.assign({}, styles.selectAll.base, styles.selectAll[viewport])}
                    >
                        <Checkbox
                            style={styles.checkbox}
                            data-test={DATATEST.GALLERY.BULK_SELECT_ALL}
                            iconStyle={isPhabletOrLess ? styles.checkboxMobile : styles.checkboxIcon}
                            labelStyle={styles.checkboxLabel}
                            checked={bulkItems.allSelected}
                            checkedIcon={bulkItems.isEmptyList() ? <CheckBox /> : <IndeterminateCheckBox />}
                            onCheck={onSelectAll}
                            label={
                                <FormatMessage
                                    path="SELECT_ALL_THAT_MEETS_CRITERIA"
                                    elements={{
                                        strong: (
                                            <strong
                                                style={{
                                                    fontWeight: "bold",
                                                }}
                                            />
                                        ),
                                    }}
                                />
                            }
                        />
                    </ToolbarGroup>

                    <ToolbarGroup
                        className="mobileBulkActionCheckbox"
                        style={Object.assign({}, styles.selectAll.base, styles.selectAll[viewport])}
                    >
                        <Checkbox
                            style={styles.checkbox}
                            iconStyle={isPhabletOrLess ? styles.checkboxMobile : styles.checkboxIcon}
                            labelStyle={styles.checkboxLabel}
                            checked={bulkItems.allSelected}
                            checkedIcon={bulkItems.isEmptyList() ? <CheckBox /> : <IndeterminateCheckBox />}
                            onCheck={onSelectAll}
                            label={format("Select all")}
                        />
                    </ToolbarGroup>

                    <ToolbarGroup>
                        <div
                            className="selectedCount"
                            style={Object.assign(
                                {},
                                styles.count.base,
                                styles.count[viewport],
                                bulkItems.allSelected && styles.countHide
                            )}
                        >
                            <FormatMessage
                                path="[strong]|count|[strong] Selected"
                                elements={{
                                    strong: (
                                        <strong
                                            style={{
                                                fontWeight: "bold",
                                                paddingRight: "2px",
                                            }}
                                        />
                                    ),
                                }}
                                variables={{
                                    count: this.displayCount,
                                }}
                            />
                        </div>
                    </ToolbarGroup>

                    {!bulkItems.allSelected && (
                        <Badge
                            className="mobileSelectedCount"
                            badgeContent={this.displayCount}
                            primary={true}
                            style={styles.mobileBadge}
                        />
                    )}

                    <ToolbarGroup style={styles.toolbarGroup}>{right}</ToolbarGroup>
                </Toolbar>
            </Paper>
        );
    }
}
export default withLDConsumer()(BulkActions);
